import { Paper } from '@mui/material'
import * as Sentry from '@sentry/react'
import { useStore } from 'easy-peasy'
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ENV, MM_WS_URL } from '../../../env'
import { useGlobalContext } from '../../../sharedComponents/src/Contexts/GlobalContext'
import ReconnectingWebSocket, {
    CloseEvent,
    ErrorEvent,
    Options,
    WS_ERROR_EVENT_STATUS,
} from '../../../socket/ReconnectingWebsocket'
import { useStoreActions, useStoreState } from '../../../store/hooks'
import { StoreModel } from '../../../store/store'
import { ConnectionStatus, MMChallengeType } from '../../../store/types'
import { GameViewState, StateChangeRequest } from '../../gameView/GameViewModel'
import { MM_ERRORS, MM_MESSAGES, MM_REQUESTS } from './MMTypes'

// MySQL deadlock / dead pooled connection on the MM side: the request was rolled back, resending is safe
const TRANSIENT_MM_ERROR = /Error 1213|\(40001\)|Deadlock found|invalid connection/

const isGameInProgress = (gameState: GameViewState) =>
    gameState === GameViewState.GAME_RUNNING || gameState === GameViewState.GAME_PRELOADING

export const MatchMakingConnector = () => {
    const store = useStore<StoreModel>()
    const serverConnectionData = useStoreState((state) => state.serverConnectionData)
    const connectionStatus = useStoreState((state) => state.matchMaker.connectionStatus)
    const token = useStoreState((state) => state.token)
    const alreadyConnected = useStoreState((state) => state.matchMaker.alreadyConnected)
    const gameInQueue = useStoreState((state) => state.matchMaker.gameInQueue)

    const resetMatchMakerState = useStoreActions((state) => state.matchMaker.reset)
    const setSocket = useStoreActions((state) => state.matchMaker.setSocket)
    const setMatchData = useStoreActions((state) => state.gameView.setMatchData)
    const setAlreadyConnected = useStoreActions((state) => state.matchMaker.setAlreadyConnected)
    const setChallenges = useStoreActions((state) => state.matchMaker.setChallenges)
    const setRematchMatchData = useStoreActions((state) => state.matchMaker.setRematchMatchData)
    const setRematchStatus = useStoreActions((state) => state.matchMaker.setRematchStatus)
    const setSnackbarMessage = useStoreActions((state) => state.setSnackbarMessage)
    const setOnlinePresence = useStoreActions((state) => state.matchMaker.setOnlinePresence)
    const setScoreboard = useStoreActions((state) => state.matchMaker.setScoreboard)
    const showGameOverDialog = useStoreActions((state) => state.matchMaker.showGameOverDialog)
    const getScore = useStoreActions((state) => state.matchMaker.getScore)
    const settlePendingRequests = useStoreActions((state) => state.matchMaker.settlePendingRequests)

    const addQueueTimeStart = useStoreActions((state) => state.addQueueTimeStart)
    const removeQueueTimeStart = useStoreActions((state) => state.removeQueueTimeStart)
    const clearQueueTimeStarts = useStoreActions((state) => state.clearQueueTimeStarts)
    const setGameToQueue = useStoreActions((state) => state.matchMaker.setGameToQueue)
    const setConnectionStatus = useStoreActions((state) => state.matchMaker.setConnectionStatus)
    const setBots = useStoreActions((state) => state.matchMaker.setBots)
    const shutdownGSConnection = useStoreActions((state) => state.gameView.shutDownGSConnection)
    const clearPostGameSocket = useStoreActions((state) => state.gameView.clearPostGameSocket)
    const resetGame = useStoreActions((state) => state.gameView.resetGame)
    const setOpponentDisconnected = useStoreActions((state) => state.gameView.setOpponentDisconnected)
    const preloadGame = useStoreActions((state) => state.gameView.preloadGame)
    const setGameEndEnabled = useStoreActions((state) => state.gameView.setGameEndEnabled)
    const acceptChallenge = useStoreActions((state) => state.matchMaker.acceptChallenge)
    const settleSentRequest = useStoreActions((state) => state.matchMaker.settleSentRequest)

    const setDrawerOpen = useStoreActions((state) => state.setDrawerOpen)
    const setIsDrawerMenuOpen = useStoreActions((state) => state.setIsDrawerMenuOpen)
    const updateQueueTimeStart = useStoreActions((state) => state.updateQueueTimeStart)
    const navigate = useNavigate()
    const { logoutHandler } = useGlobalContext()

    useEffect(() => {
        if (!token || alreadyConnected) return

        // A URL-provider function, not a frozen string — so a reconnect the class
        // triggers internally (dropped connection, appResume) re-reads the live
        // token instead of replaying whatever token this socket was built with.
        const url = () => `${MM_WS_URL}/api/ws?token=${store.getState().token}`
        const options: Options = {
            debug: true,
            debugName: 'MM',
            // Exponential backoff: 1s, 2s, 4s, 8s... up to 60s
            minReconnectionDelay: 1000,
            maxReconnectionDelay: 60000,
            reconnectionDelayGrowFactor: 2.0,
            // 30 retries (down from 600)
            maxRetries: 30,
            // TODO: Consider adding explicit keepalive config after coordinating with backend team
            // keepaliveDelay: 10000,
            // keepaliveTimeout: 5000,
            useJitter: true,
        }
        const ws = new ReconnectingWebSocket(url, undefined, options)

        // MM re-announces (after its own reconnect) the game this tab still plays on a live GS socket
        const isLiveGame = (gameServiceUrl?: string, pairId?: string) => {
            const { gameState, connectionStatus, matchData } = store.getState().gameView
            return (
                !!gameServiceUrl &&
                !!matchData &&
                matchData.gameServiceUrl === gameServiceUrl &&
                (!pairId || !matchData.matchId || pairId === matchData.matchId) &&
                isGameInProgress(gameState) &&
                connectionStatus === ConnectionStatus.CONNECTED
            )
        }

        ws.addEventListener('close', (ce: CloseEvent) => {
            console.log('matchmaker ws closed, attempting to reconnect')
            // Set to CONNECTING when connection closes and will try to reconnect
            setConnectionStatus(ConnectionStatus.CONNECTING)
        })

        ws.addEventListener('open', () => {
            setConnectionStatus(ConnectionStatus.CONNECTED)
            setSocket(ws)
            ws.send(JSON.stringify({ requestType: MM_REQUESTS.ONLINE_USERS }))
            // console.log('matchmaker ws opened')
        })

        ws.addEventListener('reconnect', () => {
            setConnectionStatus(ConnectionStatus.CONNECTED)
            setSocket(ws)
            // console.log('matchmaker ws reconnected')
        })

        ws.addEventListener('error', (ev: ErrorEvent) => {
            // TIMEOUT / empty message are transient states the reconnect logic
            // below already handles — breadcrumb, not error. Only the terminal
            // MAX_RETRIES is worth reporting.
            Sentry.addBreadcrumb({
                category: 'ws',
                level: 'info',
                message: `matchmaker error: ${ev.message || 'no message'}`,
            })
            console.error('matchmaker ws error', ev)
            resetMatchMakerState()
            // GS has its own keepalive and rejoins on reconnect: a transient MM drop must not end the game
            if (
                ev.message === WS_ERROR_EVENT_STATUS.MAX_RETRIES ||
                !isGameInProgress(store.getState().gameView.gameState)
            ) {
                shutdownGSConnection()
            }
            switch (ev.message) {
                case WS_ERROR_EVENT_STATUS.TIMEOUT:
                    setConnectionStatus(ConnectionStatus.CONNECTING)
                    break
                case WS_ERROR_EVENT_STATUS.MAX_RETRIES:
                    Sentry.captureMessage('Matchmaker connection failed after max retries', 'warning')
                    setConnectionStatus(ConnectionStatus.RECONNECT_FAILED)
                    break
                default:
                    break
            }
        })

        ws.addEventListener('message', (me) => {
            let mData: any
            try {
                mData = JSON.parse(me.data)
            } catch (e) {
                Sentry.addBreadcrumb({
                    category: 'ws',
                    level: 'warning',
                    message: 'matchmaker: unparsable frame ignored',
                })
                return
            }
            console.log('mData', mData)
            // pushes nest the GS url in data.metadata; myStatus replies carry it at data.gameServiceUrl
            const gameServiceUrl: string | undefined =
                mData.data?.metadata?.gameServiceUrl ?? mData.data?.gameServiceUrl

            if (mData.messageType === MM_MESSAGES.PLAYER_NOT_PLAYING) {
                showGameOverDialog()
            }

            // ahead of the handlers below, so one throwing on an odd frame can't leave a request pending
            settlePendingRequests(mData)

            if (
                mData.messageType === MM_MESSAGES.MATCH_CREATED ||
                mData.messageType === MM_MESSAGES.CHALLENGE_ACCEPTED ||
                (mData.messageType === MM_MESSAGES.PLAYER_IS_PLAYING &&
                    !isLiveGame(gameServiceUrl, mData.data?.result?.pairId)) ||
                (mData.requestType === MM_REQUESTS.MY_STATUS && mData.data?.isPlaying === true)
            ) {
                console.log('mm relevant game start event reveived' + JSON.stringify(mData))
                shutdownGSConnection()
                // a new game ends the previous game's post-game emotes
                clearPostGameSocket(undefined)

                if (window.location.pathname !== '/' && mData.messageType === MM_MESSAGES.PLAYER_IS_PLAYING) {
                    setGameToQueue(true)
                } else {
                    mData.messageType === MM_MESSAGES.CHALLENGE_ACCEPTED
                        ? setDrawerOpen('closed')
                        : setDrawerOpen('loading')

                    setGameToQueue(false)
                    navigate('/')

                    const result = mData.data?.result
                    const preloaded = mData.messageType === MM_MESSAGES.CHALLENGE_ACCEPTED && !!result
                    if (preloaded) {
                        const { matchKind, player1, player2 } = result
                        preloadGame(result)
                        if (matchKind === 'human_vs_human' && player1 && player2) {
                            getScore({
                                challengerId: player1.id,
                                challengeeId: player2.id,
                            })
                        }
                    }

                    if (gameServiceUrl) {
                        setMatchData({
                            matchId: result?.pairId || '',
                            gameServiceUrl,
                            // a myStatus reply resumes a game: GameServerConnector joins on open only for playerIsPlaying
                            messageType:
                                mData.requestType === MM_REQUESTS.MY_STATUS
                                    ? MM_MESSAGES.PLAYER_IS_PLAYING
                                    : mData.messageType,
                            // reconnect pushes carry result: {}; the pre-game overlay needs both players or nothing
                            result: result?.player1 && result?.player2 ? result : undefined,
                        })
                    } else {
                        Sentry.captureMessage('MM game-start message without gameServiceUrl', {
                            level: 'warning',
                            extra: { messageType: mData.messageType, requestType: mData.requestType },
                        })
                        if (preloaded) resetGame(GameViewState.PRE_GAME)
                        setDrawerOpen('open')
                    }
                }
            }

            if (mData.requestType === MM_REQUESTS.MY_STATUS && mData.data?.isPlaying === false) {
                setGameToQueue(false)
                setSnackbarMessage('in progress game ended')
            }

            if (mData.requestType === MM_REQUESTS.CHALLENGE_REMATCH) {
                if (mData.result.status === 'success') {
                    setRematchStatus(StateChangeRequest.REQUESTED)
                    setSnackbarMessage('Rematch request sent')
                } else if (mData.result?.status === 'error') {
                    setRematchStatus(StateChangeRequest.ERROR)
                }
            }

            if (mData.messageType === MM_MESSAGES.CHALLENGE_REJECTED) {
                if (mData.data.result?.isRematch) {
                    setRematchStatus(StateChangeRequest.REJECTED)
                    setSnackbarMessage('Rematch rejected')
                }
            }

            if (mData.messageType === MM_MESSAGES.CHALLENGE_REMATCH_REQUESTED) {
                setRematchMatchData(mData.data.result)
                setRematchStatus(StateChangeRequest.RECEIVED)
            }

            if (mData.messageType === MM_MESSAGES.CHALLENGE_FOUND) {
                updateQueueTimeStart({ challengeId: mData.data.result.challengeId, pairId: mData.data.result.pairId })
                acceptChallenge({ challengeType: MMChallengeType.DIRECT, id: mData.data.result.pairId })
            }

            if (mData.messageType === MM_MESSAGES.ONLINE_USERS && mData.data?.amount) {
                setOnlinePresence(mData.data.amount)
            }

            if (mData.requestType === MM_REQUESTS.CHALLENGE_SCORE) {
                if (mData.result.status === 'success') {
                    setScoreboard(mData.data)
                }
            }

            if (
                mData.requestType === MM_REQUESTS.CHALLENGE_ACCEPT ||
                mData.requestType === MM_REQUESTS.CHALLENGE_CREATE ||
                mData.requestType === MM_REQUESTS.CHALLENGE_DELETE ||
                mData.requestType === MM_REQUESTS.CHALLENGE_REJECT
            ) {
                if (mData.data?.result?.isRematch && mData.requestType === MM_REQUESTS.CHALLENGE_REJECT) {
                    setRematchStatus(StateChangeRequest.REJECTED)
                }
                const rawError: string = mData.result?.errorMessage ?? ''
                const retrying = settleSentRequest({
                    requestType: mData.requestType,
                    id: mData.data?.pairId || mData.data?.challengeId,
                    retry: mData.result?.status === 'error' && TRANSIENT_MM_ERROR.test(rawError),
                })
                if (retrying) {
                    Sentry.addBreadcrumb({
                        category: 'ws',
                        level: 'warning',
                        message: `matchmaker ${mData.requestType} failed, retrying once: ${rawError}`,
                    })
                } else if (mData.result?.status === 'error') {
                    const action =
                        mData.requestType === MM_REQUESTS.CHALLENGE_ACCEPT ||
                        mData.requestType === MM_REQUESTS.CHALLENGE_CREATE
                            ? 'Start'
                            : 'Decline'
                    const isOurRematchAccept =
                        mData.requestType === MM_REQUESTS.CHALLENGE_ACCEPT &&
                        !!mData.data?.pairId &&
                        mData.data.pairId === store.getState().matchMaker.rematchMatchData.pairId
                    // A retried rematch accept can fail after the first attempt already started the game
                    const rematchStarted = isOurRematchAccept && isGameInProgress(store.getState().gameView.gameState)
                    if (mData.data?.isRematch && !rematchStarted) {
                        setRematchStatus(StateChangeRequest.ERROR)
                    }
                    // AppLayout closes the game-over panel when the rematch is accepted
                    if (isOurRematchAccept && !rematchStarted && !rawError.includes('already approved')) {
                        setGameEndEnabled(true)
                    }
                    if (rematchStarted) {
                        Sentry.addBreadcrumb({
                            category: 'ws',
                            level: 'info',
                            message: `matchmaker ${mData.requestType} failed after the rematch started: ${rawError}`,
                        })
                    } else if (mData.result.code === MM_ERRORS.PLAYER_IS_SUSPENDED) {
                        setSnackbarMessage(
                            `Your account has been suspended. Reach Customer Service for additional information.`,
                        )
                    } else if (mData.result.code === MM_ERRORS.OPPONENT_IS_SUSPENDED) {
                        setSnackbarMessage(`Opponent not available`)
                    } else if (mData.result.code === MM_ERRORS.BOT_LOCKED) {
                        setSnackbarMessage(`Bot Unavailable`)
                    } else if (rawError === 'player is offline') {
                        setSnackbarMessage(`Unable to ${action} Match: User is Offline`)
                    } else if (rawError === 'player is playing') {
                        setSnackbarMessage(`Unable to ${action} Match: User is Currently Playing`)
                    } else if (rawError.includes('limit for direct challenges is reached')) {
                        setSnackbarMessage(`Limit: You Can Only Have 2 Direct Challenges Active`)
                    } else if (rawError.includes('limit for custom challenges is reached')) {
                        setSnackbarMessage(`Limit: You Can Only Have 3 Custom Challenges Active`)
                    } else if (rawError.includes('not found') || rawError.includes('not available')) {
                        setSnackbarMessage('Challenge not available')
                        const id = mData.data.pairId || mData.data.challengeId || mData.data.customId
                        const key =
                            mData.requestType === MM_REQUESTS.CHALLENGE_ACCEPT ? 'challengesToMe' : 'challengesByMe'
                        // @ts-ignore - this is a hack to get the challenge to hide in the UI
                        setChallenges({
                            [key]: [
                                {
                                    challengeId: id,
                                    pairId: id,
                                    challengeType: mData.data.pairId ? MMChallengeType.DIRECT : MMChallengeType.CUSTOM,
                                    recordState: 2, // DELETE
                                },
                            ],
                        })
                    } else if (rawError.includes('already approved')) {
                        // a repeated accept while the first one is processed: the game may already be starting
                        Sentry.addBreadcrumb({
                            category: 'ws',
                            level: 'info',
                            message: `matchmaker ${mData.requestType}: ${rawError}`,
                        })
                    } else {
                        // Expected races / concurrent-operation responses — surface to
                        // the user, but don't report them as errors.
                        const isExpected =
                            rawError.includes('already playing') ||
                            rawError.includes('is busy with another operation') ||
                            rawError.includes("can't be deleted")
                        if (isExpected) {
                            setSnackbarMessage(`Unable to ${action} Match. Please try again.`)
                        } else {
                            // Genuinely unexpected (e.g. BE "internal server error",
                            // MySQL deadlock). Fingerprint WITHOUT the volatile
                            // requestId so these group into one issue per error type
                            // instead of fragmenting into dozens; keep requestId as
                            // debug extra.
                            Sentry.captureMessage(`Unable to ${action} Match: ${rawError}`, {
                                level: 'error',
                                fingerprint: ['mm-op-failed', action, rawError],
                                tags: { mm_request: mData.requestType },
                                extra: { requestId: mData.requestId },
                            })
                            setSnackbarMessage(
                                action === 'Start'
                                    ? "Couldn't start the game. Please try again."
                                    : "Couldn't update the challenge. Please try again.",
                            )
                        }
                    }
                }
            }

            if (mData.requestType === MM_REQUESTS.MY_CHALLENGES && mData.data) {
                setChallenges(mData.data)
            }

            if (mData.messageType === MM_MESSAGES.ALREADY_CONNECTED) {
                setAlreadyConnected(true)
                if (window.location.pathname === '/') {
                    if (gameInQueue) {
                        setDrawerOpen('open')
                        setIsDrawerMenuOpen(true)
                    }
                } else {
                    resetGame(GameViewState.PRE_GAME)
                    setOpponentDisconnected(undefined)
                }
            }

            if (mData.messageType === MM_MESSAGES.CHALLENGE_CREATED) {
                if (mData.data.challengeType === MMChallengeType.DIRECT) {
                    setSnackbarMessage(`Challenge sent, match will start once the user accepts`)
                }

                // list of open challenges for tracking time in queue
                const queueTimeStart = {
                    challengeId: mData.data.result.challengeId,
                    challengeType: mData.data.result.challengeType,
                    timeMode: `${mData.data.result.timeMode?.durationMinutes || 0}-${mData.data.result.timeMode?.clockIncrementSeconds || 0}`,
                    timestamp: new Date().getTime(),
                    rated: mData.data.result.rated,
                }

                addQueueTimeStart(queueTimeStart)

                if (mData.data.result.challengeType === MMChallengeType.AUTO) {
                    setChallenges({
                        challengesByMe: [
                            // @ts-ignore - this is a hack to turn on the challenge UI animation
                            {
                                challengeId: mData.data.result.challengeId,
                                challengeType: mData.data.result.challengeType,
                                rated: mData.data.result.rated,
                                timeMode: mData.data.result.timeMode,
                                ratingRange: { from: mData.data.result.ratingFrom, to: mData.data.result.ratingTo },
                                recordState: 1,
                            },
                        ],
                    })
                }
            }

            if (mData.messageType === MM_MESSAGES.CHALLENGE_DELETED) {
                removeQueueTimeStart(mData.data.challengeId)
                setChallenges({
                    challengesByMe: [
                        // @ts-ignore - this is a hack to turn off the challenge UI animation
                        {
                            challengeId: mData.data.rawData.challengeId,
                            challengeType: mData.data.rawData.challengeType,
                            recordState: 2, // DELETE
                        },
                    ],
                })
            }

            if (mData.messageType === MM_MESSAGES.CHALLENGE_COMPLETED) {
                // reset game information here
            }

            if (mData.requestType === MM_REQUESTS.ONLINE_USERS && mData.data?.online_users) {
                setOnlinePresence(mData.data.online_users)
            }

            if (mData.messageType === MM_MESSAGES.REFRESH_PAGE) {
                window.location.reload()
            }

            if (mData.messageType === MM_MESSAGES.LOGOUT) {
                logoutHandler('server_push')
            }

            if (mData.requestType === MM_REQUESTS.BOTS_LIST) {
                if (mData.result.status === 'success') {
                    setBots(mData.data)
                }
            }
        })

        return () => {
            ws.close()
            resetMatchMakerState()
        }
        // Only depend on values that determine WHETHER to connect, not HOW to handle events
        // Store actions are stable references and shouldn't trigger reconnections
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, alreadyConnected])

    return (
        <Paper
            className={'serverConnection'}
            style={{
                display: serverConnectionData ? 'block' : 'none',
            }}
        >
            MM: {connectionStatus} {MM_WS_URL} {ENV}{' '}
        </Paper>
    )
}
